import { useContext, useMemo } from "react";

import {
  CommandStatus,
  CommandLang,
  convertCountryNameToCountryCode,
  CountryNames,
  buildFulfillmentCheckout,
} from "@simplyk/common";
import { DateTime } from "luxon";
import { Control, useFormState, useWatch } from "react-hook-form";

import { FrontendFormContext } from "../contexts/FrontendFormContext";
import { useLocaleContext } from "../contexts/LocaleContext";

import { useUpsertCommand } from "./useUpsertCommand";

import { serializeProductTickets } from "@/common/helpers/command";
import { getFulfillmentShippingFields } from "@/common/helpers/fulfillmentCheckout";
import { getRateSpecificDiscountAmount } from "@/common/helpers/order";
import { computeTicketsPrice, getEligibleAmountTotal } from "@/common/helpers/rates";
import { DiscountWithRateMappings, TicketingFrontendPageOutput } from "@/common/types/trpc";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { TicketingFormData } from "@/components/TicketingDrawers/type";

interface TicketingFormUpdatedProps {
  control: Control<TicketingFormData>;
  ticketing: TicketingFrontendPageOutput;
  validDiscount: DiscountWithRateMappings | null;
}

export const useTicketingFormUpdated = ({ control, ticketing, validDiscount }: TicketingFormUpdatedProps) => {
  const { isDirty, isSubmitting, isSubmitSuccessful } = useFormState({ control });
  const { locale } = useLocaleContext();
  const {
    tip,
    displayedFormAmount,
    selectedPaymentMethod,
    formData,
    formType,
    organization,
    shouldSendTipPercentage,
    selectedTip,
    displayAddressQuestion,
  } = useContext(FrontendFormContext);

  const email = useWatch({ control, name: "email" });
  const firstName = useWatch({ control, name: "firstName" });
  const lastName = useWatch({ control, name: "lastName" });
  const address = useWatch({ control, name: "address" });
  const city = useWatch({ control, name: "city" });
  const postalCode = useWatch({ control, name: "postalCode" });
  const region = useWatch({ control, name: "region" });
  const formCountry = useWatch({ control, name: "country" });
  const discountId = useWatch({ control, name: "discountId" });
  const extraDonation = useWatch({ control, name: "extraDonation" });
  const isCorporate = useWatch({ control, name: "giveAsOrganism" });
  const corporationName = useWatch({ control, name: "companyName" });
  const ticketsPurchased = useWatch({ control, name: "ticketsPurchased" });
  const occurrenceId = useWatch({ control, name: "occurrenceId" });
  const fulfillmentMethod = useWatch({ control, name: "fulfillmentMethod" });
  const shippingFullName = useWatch({ control, name: "shippingFullName" });
  const shippingAddress = useWatch({ control, name: "shippingAddress" });
  const shippingCity = useWatch({ control, name: "shippingCity" });
  const shippingPostalCode = useWatch({ control, name: "shippingPostalCode" });
  const shippingRegion = useWatch({ control, name: "shippingRegion" });
  const shippingCountry = useWatch({ control, name: "shippingCountry" });
  const shippingSameAsBilling = useWatch({ control, name: "shippingSameAsBilling" });
  const shippingSameFullNameAsBuyer = useWatch({ control, name: "shippingSameFullNameAsBuyer" });

  const fulfillmentShippingFields = useMemo(
    () =>
      getFulfillmentShippingFields(
        {
          firstName,
          lastName,
          companyName: corporationName,
          address,
          city,
          postalCode,
          region,
          country: formCountry,
          shippingSameAsBilling,
          shippingSameFullNameAsBuyer,
          shippingFullName,
          shippingAddress,
          shippingCity,
          shippingPostalCode,
          shippingRegion,
          shippingCountry,
        } as TicketingFormData,
        { canOfferSameAsBilling: displayAddressQuestion, canOfferSameFullNameAsBuyer: !displayAddressQuestion }
      ),
    [
      firstName,
      lastName,
      corporationName,
      address,
      city,
      postalCode,
      region,
      formCountry,
      shippingSameAsBilling,
      shippingSameFullNameAsBuyer,
      shippingFullName,
      shippingAddress,
      shippingCity,
      shippingPostalCode,
      shippingRegion,
      shippingCountry,
      displayAddressQuestion,
    ]
  );

  const { fulfillmentCheckout, shippingFeeAmount } = buildFulfillmentCheckout({
    formCategory: ticketing.formCategory,
    offeredMethods: ticketing.offeredFulfillmentMethods ?? [],
    selectedMethod: fulfillmentMethod ?? null,
    ticketingShippingFeeAmount: ticketing.shippingFeeAmount ?? 0,
    shippingFields: fulfillmentShippingFields,
  });

  const country = useMemo(
    () => (formCountry ? convertCountryNameToCountryCode(formCountry as CountryNames) : undefined),
    [formCountry]
  );

  const discountAmount = useMemo(() => {
    if (!ticketing.rates || !ticketsPurchased || !validDiscount) {
      return 0;
    }

    return getRateSpecificDiscountAmount(
      validDiscount,
      ticketsPurchased,
      ticketing.rates,
      validDiscount.selectedRateIds || []
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validDiscount, JSON.stringify(ticketsPurchased)]);

  const eligibleAmount = useMemo(
    () =>
      getEligibleAmountTotal(
        ticketsPurchased,
        ticketing,
        extraDonation || 0,
        validDiscount,
        validDiscount?.selectedRateIds ?? []
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [extraDonation, validDiscount, JSON.stringify(ticketsPurchased), JSON.stringify(ticketing.rates)]
  );

  const productTickets = serializeProductTickets(ticketsPurchased);
  const ticketsPrice = ticketing.rates ? computeTicketsPrice(ticketing.rates, ticketsPurchased) : 0;

  useUpsertCommand(
    {
      email,
      firstName,
      lastName,
      address,
      city,
      postalCode,
      region,
      country,
      discountAmount,
      eligibleAmount,
      extraDonation,
      discountId,
      isCorporate,
      corporationName,
      ticketingOccurrenceId: occurrenceId,
      tipAmount: (!shouldSendTipPercentage ? tip : 0) || 0,
      tipPercentage: (shouldSendTipPercentage ? selectedTip.percentage : 0) || 0,
      productsAmount: ticketsPrice,
      totalAmount: displayedFormAmount + tip,
      shippingFeeAmount,
      paymentMethod: selectedPaymentMethod,
      productTickets,
      productDonation: null,
      productBids: null,
      formId: formData.id,
      formLang: CommandLang[locale],
      formType: formType || null,
      locale,
      organizationId: organization.id,
      status: CommandStatus.Open,
      userTimezone: DateTime.local().zoneName || "UTC",
      fulfillmentCheckout,
    },
    isDirty,
    isSubmitting || isSubmitSuccessful
  );
};
